/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, isEmpty, Text, VFlow } from 'bold-ui'
import { resolveName, SimNaoEnum } from 'components/form'
import { SimNaoRadioGroupBoxField } from 'components/form/field/SimNaoRadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { ExemplosHorizontalField, ExemplosHorizontalFieldOptions } from '../../components/ExemplosHorizontalField'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum } from '../../model-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta05ExemplosEnum,
  ConsultaSeguimentoPergunta05Model,
} from './model-consultaSeguimentoPergunta05'
import {
  hasPergunta05ExemplosFalhouAnySim,
  hasPergunta05ExemplosPassouAnySim,
  isPergunta05ExemplosAllNao,
  isPergunta05ExemplosFalhouAllFilled,
  isPergunta05ExemplosFalhouAllNao,
} from './utils-consultaSeguimentoPergunta05'

interface ConsultaSeguimentoPergunta05FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta05Model>
}

export function ConsultaSeguimentoPergunta05Field(props: Readonly<ConsultaSeguimentoPergunta05FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta05Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta05Model>({
    name,
    conditionToCalculate,
    calculateResultado,
  })

  const hasAnySimExemplosPassou = hasPergunta05ExemplosPassouAnySim(value.PERGUNTA_05_EXEMPLOS)
  const hasAnySimExemplosFalhou = hasPergunta05ExemplosFalhouAnySim(value.PERGUNTA_05_EXEMPLOS)
  const isExemplosFalhouAllNao = isPergunta05ExemplosFalhouAllNao(value.PERGUNTA_05_EXEMPLOS)
  const isExemplosFalhouAllFilled = isPergunta05ExemplosFalhouAllFilled(value?.PERGUNTA_05_EXEMPLOS)
  const hasAnySimInFalhouOrBoth = hasAnySimExemplosFalhou || (hasAnySimExemplosPassou && hasAnySimExemplosFalhou)

  const textResultadoPassou =
    value.PERGUNTA_05_HEADER === SimNaoEnum.SIM &&
    hasAnySimExemplosPassou &&
    isExemplosFalhouAllNao &&
    'Sim para pelo menos um exemplo Passou e Não para todos exemplos Falhou:'

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_05}
          title={
            <ConsultaSeguimentoPerguntaHeaderTitle sufix=' faz movimentos estranhos com os dedos perto dos olhos dele?' />
          }
          status={value.status}
          field={<SimNaoSwitchButtonField name={name.PERGUNTA_05_HEADER} />}
        />
        {value.PERGUNTA_05_HEADER === SimNaoEnum.SIM && (
          <VFlow vSpacing={0}>
            <VFlow>
              <VFlow vSpacing={0}>
                <ConsultaSeguimentoIndicatorStart />
                <ConsultaSeguimentoIndicatorHelper
                  title='Por favor, descreva esses movimentos.'
                  subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
                />
              </VFlow>
              <VFlow vSpacing={0}>
                <ExemplosHorizontalField
                  name={name.PERGUNTA_05_EXEMPLOS}
                  nameOutro={name.OUTRO}
                  label='Responda os exemplos Passou e Falhou até pontuar na Pergunta 5 ou avançar para o próximo fluxo '
                  options={PERGUNTA_05_EXEMPLOS_OPTIONS}
                  showIndicator={isExemplosFalhouAllFilled && hasAnySimInFalhouOrBoth && PassouFalhouEnum.FALHOU}
                  showTagOptional={{ PASSOU: hasAnySimExemplosPassou }}
                />
                {hasAnySimInFalhouOrBoth && isExemplosFalhouAllFilled && (
                  <VFlow vSpacing={0}>
                    <ConsultaSeguimentoIndicatorStep
                      hideConnector
                      text={
                        <VFlow vSpacing={0}>
                          <Text color='inherit'>Sim para qualquer exemplo Falhou:</Text>
                          <Heading level={3} color='inherit'>
                            Pergunte com que frequência estes exemplos com 'sim' ocorrem
                          </Heading>
                        </VFlow>
                      }
                      style={styles.indicatorStep}
                    />
                    <SimNaoRadioGroupBoxField
                      name={name.PERGUNTA_05_DUAS_VEZES_POR_SEMANA}
                      label='Isso acontece mais de duas vezes por semana?'
                      required
                    />
                  </VFlow>
                )}
              </VFlow>
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          resultado={value.resultado}
          text={{ PASSOU: textResultadoPassou }}
          style={styles.indicatorResultado}
        />
      )}
    </VFlow>
  )
}

const conditionToCalculate = (values: ConsultaSeguimentoPergunta05Model): boolean => {
  if (values.PERGUNTA_05_HEADER === SimNaoEnum.NAO) {
    return true
  }

  const hasAnySimExemplosPassou = hasPergunta05ExemplosPassouAnySim(values?.PERGUNTA_05_EXEMPLOS)
  const hasAnySimExemplosFalhou = hasPergunta05ExemplosFalhouAnySim(values?.PERGUNTA_05_EXEMPLOS)
  const isExemplosAllNao = isPergunta05ExemplosAllNao(values?.PERGUNTA_05_EXEMPLOS)
  const isExemplosFalhouAllNao = isPergunta05ExemplosFalhouAllNao(values?.PERGUNTA_05_EXEMPLOS)
  const isExemplosPassouEmpty = isEmpty(values?.PERGUNTA_05_EXEMPLOS?.PASSOU)

  if (
    (hasAnySimExemplosPassou && isExemplosFalhouAllNao) ||
    (isExemplosFalhouAllNao && isExemplosPassouEmpty) ||
    isExemplosAllNao
  ) {
    return true
  }

  if ((hasAnySimExemplosPassou && hasAnySimExemplosFalhou) || hasAnySimExemplosFalhou) {
    return !isEmpty(values?.PERGUNTA_05_DUAS_VEZES_POR_SEMANA)
  }

  return false
}

const calculateResultado = (values: ConsultaSeguimentoPergunta05Model): PassouFalhouEnum => {
  if (values.PERGUNTA_05_HEADER === SimNaoEnum.NAO) {
    return PassouFalhouEnum.PASSOU
  } else {
    const hasAnySimExemplosPassou = hasPergunta05ExemplosPassouAnySim(values.PERGUNTA_05_EXEMPLOS)
    const isExemplosAllNao = isPergunta05ExemplosAllNao(values?.PERGUNTA_05_EXEMPLOS)
    const isExemplosFalhouAllNao = isPergunta05ExemplosFalhouAllNao(values.PERGUNTA_05_EXEMPLOS)
    const isExemplosPassouEmpty = isEmpty(values?.PERGUNTA_05_EXEMPLOS?.PASSOU)

    if (
      (hasAnySimExemplosPassou && isExemplosFalhouAllNao) ||
      (isExemplosFalhouAllNao && isExemplosPassouEmpty) ||
      isExemplosAllNao
    ) {
      return PassouFalhouEnum.PASSOU
    }

    if (values.PERGUNTA_05_DUAS_VEZES_POR_SEMANA === SimNaoEnum.SIM) {
      return PassouFalhouEnum.FALHOU
    } else if (values.PERGUNTA_05_DUAS_VEZES_POR_SEMANA === SimNaoEnum.NAO) {
      return PassouFalhouEnum.PASSOU
    }
  }
}

const PERGUNTA_05_EXEMPLOS_OPTIONS: ExemplosHorizontalFieldOptions<ConsultaSeguimentoPergunta05ExemplosEnum> = {
  PASSOU: [
    {
      label: 'Olha para as mãos?',
      value: ConsultaSeguimentoPergunta05ExemplosEnum.OLHA_PARA_AS_MAOS,
    },
    {
      label: 'Move os dedos quando brinca de mostrar e esconder o rosto?',
      value: ConsultaSeguimentoPergunta05ExemplosEnum.MOVE_OS_DEDOS_QUANDO_BRINCA_DE_ESCONDER_ROSTO,
    },
  ],
  FALHOU: [
    {
      label: 'Movimenta os dedos perto dos olhos?',
      value: ConsultaSeguimentoPergunta05ExemplosEnum.MOVIMENTA_OS_DEDOS_PERTO_DOS_OLHOS,
    },
    {
      label: 'Mantém as mãos elevadas perto dos olhos?',
      value: ConsultaSeguimentoPergunta05ExemplosEnum.MANTEM_AS_MAOS_ELEVADAS_PERTO_DOS_OLHOS,
    },
    {
      label: 'Mantém as mãos ao lado dos olhos?',
      value: ConsultaSeguimentoPergunta05ExemplosEnum.MANTEM_AS_MAOS_AO_LADO_DOS_OLHOS,
    },
    {
      label: 'Agita as mãos perto do rosto?',
      value: ConsultaSeguimentoPergunta05ExemplosEnum.AGITA_AS_MAOS_PERTO_DO_ROSTO,
    },
  ],
}

const styles = {
  indicatorStep: css`
    width: auto;
  `,
  indicatorResultado: css`
    max-width: 18rem;
  `,
}
